import { css } from '@emotion/react';
import { PhoneConfigOrg } from '@wetags/phone-config';
import { SchemaBlockedNumberService } from '@frontend/api-call-block';
import { ObjectTypeCallBlocking } from '@frontend/api-phone-audits';
import { Trans, useTranslation } from '@frontend/i18n';
import { useHasPhoneSettingsAccess, usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useAppScopeStore, useScopedQuery } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { TextLink, Text, Heading, useModalControl } from '@frontend/design-system';
import { SeeHistoryButton } from '../../components/common/see-history-button';
import { DetailsHeader } from '../../components/phone-system-history/history-details-tray/details-header';
import {
  HISTORY_TRAY_WIDTH,
  HistoryDetailsTray,
} from '../../components/phone-system-history/history-details-tray/history-details-tray';
import { PhoneSettingsPage } from '../../components/settings/settings-page';
import { queryKeys } from '../../query-keys';
import { NewBlockedNumbersTable } from '../call-blocking';

export const CallBlockingSettings = () => {
  const { t } = useTranslation('phone');
  const { settingsTenantLocation, associatedLocationIds } = usePhoneSettingsShallowStore(
    'settingsTenantLocation',
    'associatedLocationIds'
  );
  const tenantId = settingsTenantLocation?.phoneTenantId ?? '';
  const { accessibleLocationData } = useAppScopeStore();
  const selectedLocations = (associatedLocationIds ?? []).map((id) => accessibleLocationData[id]).filter(Boolean);
  const { hasPhoneSystemHistoryAccess } = useHasPhoneSettingsAccess({ selectedLocations });

  const [phoneSystemHistoryEnabled] = PhoneConfigOrg.useWetag('phone-system-history');
  const historyTray = useModalControl();

  const { data } = useScopedQuery({
    queryKey: [tenantId, ...queryKeys.settings.listCallBlock()],
    queryFn: () =>
      SchemaBlockedNumberService.ListBlockedNumbers({ tenantId: tenantId }).then((res) => res.blockedNumbers),
    meta: {
      error: {
        message: 'CallBlockingSettings:listCallBlockQuery | Failed to fetch blocked numbers',
        data: { tenantId },
      },
    },
  });

  return (
    <>
      <PhoneSettingsPage
        title={t('Call Blocking')}
        subtitle={
          <>
            <Text>{t('Block specific phone numbers that you no longer want to contact your office.')}</Text>
            <Text>
              <Trans t={t}>
                For more information see{' '}
                <TextLink
                  rel='noreferrer'
                  target='_blank'
                  href='https://www.weavehelp.com/weavehelp/article/block-calls'
                >
                  Weave Help
                </TextLink>
                .
              </Trans>
            </Text>
          </>
        }
        breadcrumbs={[
          { label: t('Phone'), to: '/phone/main' },
          { label: t('Call Blocking'), to: '/phone/call-blocking' },
        ]}
        action={
          phoneSystemHistoryEnabled &&
          hasPhoneSystemHistoryAccess && (
            <SeeHistoryButton
              isActive={historyTray.modalProps.show}
              onClick={historyTray.modalProps.show ? historyTray.closeModal : historyTray.openModal}
            />
          )
        }
        css={css`
          width: ${historyTray.modalProps.show ? `calc(100% - ${HISTORY_TRAY_WIDTH}px)` : '100%'};
          transition: width 0.3s ease;
        `}
      >
        <Heading level={2} css={{ marginBottom: theme.spacing(2) }}>
          {t('Blocked Numbers')}
        </Heading>
        <NewBlockedNumbersTable data={data ?? []} phoneTenantId={tenantId} />
      </PhoneSettingsPage>

      <HistoryDetailsTray
        modalControls={historyTray}
        tenantId={tenantId}
        callObjectType={ObjectTypeCallBlocking}
        HeaderComponent={<DetailsHeader objectType={ObjectTypeCallBlocking} onClose={historyTray.closeModal} />}
      />
    </>
  );
};
